<script>
import Vue from "vue";
import store from "@/state/store";

import { required, minLength, maxLength, alphaNum, helpers } from "vuelidate/lib/validators";
const alphaNumAndUnderScore = helpers.regex('alphaNumAndUnderScore', /^[a-z\d_]*$/i);

import { mapState } from "vuex";
import appConfig from "@/app.config";
import Layout from "@/router/layouts/auth";
import {
  AccountStatus,
  notificationMethods, SessionLoginState
} from "@/state/helpers";

/**
 * Forgot Password component
 */
export default {
  page: {
    title: "Account setup",
    meta: [{ name: "description", content: appConfig.description }]
  },
  components: { Layout },
  data() {
    return {
      username: "",
      submitted: false
    };
  },
  computed: {
    notification() {
      return this.$store ? this.$store.state.notification : null;
    }
  },
  validations: {
    username: { required, minLength: minLength(3), maxLength: maxLength(24), alphaNumAndUnderScore: alphaNumAndUnderScore }
  },
  methods: {
    ...notificationMethods,
    handle_unavailable_username() {
      this.submitted = false;
      this.$swal( {
        icon: 'error',
        title: '',
        text: this.$t('setup.username.errors.unavailable')
      });
    },
    handle_taken() {
      this.submitted = false;
      this.$swal( {
        icon: 'warning',
        title: '',
        text: this.$t('setup.username.errors.taken')
      });
    },
    handle_fail_and_reset() {
      this.submitted = false;
      this.$swal( {
        icon: 'error',
        title: this.$t('error.server.generic.title'),
        text: this.$t('error.server.generic.message')
      });
    },
    handle_rate_limit_error() {
      this.submitted = false;
      this.$swal( {
        icon: 'warning',
        title: this.$t('error.server.ratelimit.title'),
        text: this.$t('error.server.ratelimit.message')
      });
    },
    handle_success(username) {
      const router = this.$router; // Promise hell...
      this.$swal({
        icon: 'success',
        title: this.$t('setup.username.success.title', {username: username}),
        text: this.$t('setup.username.success.message'),
        confirmButtonText: this.$t('generic.finishup')
      }).then(function() {
        router.push({name: "setup_account"}).catch((error) => {});
      });
    },
    try_submit() {
      if(this.submitted) return;
      this.submitted = true;

      this.$v.$touch();
      if(this.$v.$invalid) {
        this.submitted = false;
      } else {
        fetch(process.env.VUE_APP_ROOT_API + 'v1/@me/acsrf-token', {credentials: 'include'})
        .then(response => {
          if(response.ok){
            return response.json();
          }
        })
        .then(data => {
          /* Actual request */
          let payload = {
            acsrf_token: data.acsrf_token,
            username: this.username
          };
          fetch(process.env.VUE_APP_ROOT_API + 'v1/@me/update-username', {
            method: 'POST',
            body: JSON.stringify(payload),
            credentials: 'include'
          })
              .then(response => {
                return response.json();
              })
              .then(data => {
                if(!data.status) {
                  if(data.error == 'invalid-state') {
                    /* Local state got desynchronized, reevaluating */
                    console.log('Resolving state desynchronization');
                    this.$router.push({name: "login"}).catch((error) => {});
                  } else if(data.error == 'rate-limited') this.handle_rate_limit_error();
                    else if(data.error == 'unavailable') this.handle_unavailable_username();
                    else if(data.error == 'taken') this.handle_taken();
                    else this.handle_fail_and_reset();
                }
                else this.handle_success(data.username);
              })
              .catch(error => {
                console.log(`[ERROR] ${error}`);
                this.handle_fail_and_reset();
              });
          /* --- */
        })
        .catch(error => {
          console.log(error);
          this.handle_fail_and_reset();
        });
      }
    }
  }
};
</script>

<template>
  <Layout>
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6 col-xl-5">
        <div class="card overflow-hidden">
          <div class="bg-soft-primary">
            <div class="row">
              <div class="col-12">
                <div class="text-primary p-3">
                  <h4 class="text-white text-right p-0">{{$t('setup.username.title')}}</h4>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body pt-0">
            <div>
              <router-link tag="a" to="/">
                <div class="avatar-md profile-user-wid mb-4">
                  <span class="avatar-title rounded-circle bg-light">
                    <img src="@/assets/images/logo.svg" alt height="34" />
                  </span>
                </div>
              </router-link>
            </div>
            <div
                v-if="notification.message"
                :class="'alert ' + notification.type"
            >{{notification.message}}</div>
            <h5 class="text-center">{{$t('setup.username.info')}}</h5>
            <div class="p-2">
              <form @submit.prevent="try_submit">
                <div class="form-group">
                  <label for="username">{{$t('generic.username')}}</label>
                  <input
                    type="text"
                    v-model.trim="username"
                    class="form-control"
                    id="username"
                    :class="{ 'is-invalid': $v.username.$error }"
                    @change="$v.username.$touch()"
                  />
                  <div v-if="$v.username.$error" class="invalid-feedback">
                    <span v-if="!$v.username.required">{{$t('setup.username.errors.required')}}</span>
                    <span v-if="!$v.username.alphaNumAndUnderScore">{{$t('setup.username.errors.invalid')}}</span>
                    <span v-if="!$v.username.minLength">{{$t('setup.username.errors.length')}}</span>
                    <span v-if="!$v.username.maxLength">{{$t('setup.username.errors.length')}}</span>
                  </div>
                </div>
                <div class="mt-3">
                  <b-button type="submit" variant="primary" class="btn-block">{{$t('generic.continue')}}</b-button>
                </div>
              </form>
            </div>
          </div>
          <!-- end card-body -->
        </div>
        <!-- end card -->
      </div>
      <!-- end col -->
    </div>
    <!-- end row -->
    <div class="mt-3 text-center">
      <small>
        © 2017 - {{new Date().getFullYear()}} {{$t('generic.legal_disclaimer')}} {{$t('generic.provider')}}<br>
        <small style="font-size: 10px;">
          <a href="https://cftools.cloud/legal/imprint" style="color: unset !important;">Imprint</a>
          /
          <a href="https://cftools.cloud/legal/privacy" style="color: unset !important;">Privacy policy</a>
        </small>
      </small>
    </div>
  </Layout>
</template>

<style lang="scss" module></style>
